import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Loader2, Search, ClipboardList, Eye, X, CheckCircle, ShieldCheck, Ban, AlertTriangle } from 'lucide-react';
import MemberLookupDialog from './MemberLookupDialog';
import VisualTimeline from './VisualTimeline';

const STATUS_BADGES = {
  assigned: { label: 'Assigned', className: 'bg-gray-100 text-gray-700' },
  tl_verified: { label: 'TL Verified', className: 'bg-blue-100 text-blue-700' },
  manager_verified: { label: 'Manager Approved', className: 'bg-indigo-100 text-indigo-700' },
  shreya_verified: { label: 'Shreya Verified', className: 'bg-orange-100 text-orange-700' },
  admin_approved: { label: 'Vishal Approved', className: 'bg-emerald-100 text-emerald-700' },
  ops_delivered: { label: 'Ops Delivered', className: 'bg-purple-100 text-purple-700' },
  customer_received: { label: 'Customer Received', className: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700' },
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'tl_verified', label: 'TL Verified' },
  { value: 'manager_verified', label: 'Manager Approved' },
  { value: 'shreya_verified', label: 'Shreya Verified' },
  { value: 'admin_approved', label: 'Vishal Approved' },
  { value: 'ops_delivered', label: 'Ops Delivered' },
  { value: 'customer_received', label: 'Customer Received' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatINR = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
};

// Extract completed verification stages with notes for inline display
function getVerificationTrail(a) {
  const stages = [];
  if (a.tl_verified_at) {
    stages.push({ label: 'TL', name: a.tl_verified_by_name, notes: a.tl_verification_notes, at: a.tl_verified_at, color: 'text-blue-600' });
  }
  if (a.manager_verified_at) {
    stages.push({ label: 'Mgr', name: a.manager_verified_by_name, notes: a.manager_verification_notes, at: a.manager_verified_at, color: 'text-indigo-600' });
  }
  if (a.shreya_verified_at) {
    stages.push({ label: 'Shreya', name: a.shreya_verified_by_name, notes: a.shreya_verification_notes, at: a.shreya_verified_at, color: 'text-orange-600' });
  }
  if (a.admin_approved_at) {
    stages.push({ label: 'Admin', name: a.admin_approved_by_name, notes: a.admin_approval_notes, at: a.admin_approved_at, color: 'text-emerald-600' });
  }
  // Show rejection info if present
  if (a.last_shreya_rejection_reason) {
    stages.push({ label: 'Shreya Reject', name: a.last_shreya_rejected_by_name, notes: a.last_shreya_rejection_reason, at: a.last_shreya_rejected_at, color: 'text-red-600', isRejection: true });
  }
  if (a.last_admin_rejection_reason) {
    stages.push({ label: 'Admin Reject', name: a.last_admin_rejected_by_name, notes: a.last_admin_rejection_reason, at: a.last_admin_rejected_at, color: 'text-red-600', isRejection: true });
  }
  return stages;
}

function getSlaStatus(assignment) {
  if (!assignment.sla_deadline) return null;
  if (!['admin_approved', 'ops_delivered'].includes(assignment.status)) return null;
  const now = new Date();
  const deadline = new Date(assignment.sla_deadline);
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return { overdue: true, text: `OVERDUE by ${Math.abs(diffDays)}d`, className: 'text-red-600 font-semibold animate-pulse' };
  }
  return { overdue: false, text: `${diffDays}d remaining`, className: 'text-green-600' };
}

export default function AssignmentTab({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [stacks, setStacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', gym_id: '' });

  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showMemberLookup, setShowMemberLookup] = useState(false);
  const [createForm, setCreateForm] = useState({
    customer_name: '', customer_phone: '', customer_email: '',
    gym_name: '', gym_id: '', stack_id: '', notes: '',
    membership_plan: '', membership_start: '', membership_expiry: '', membership_price: 0,
    membership_sold_by: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Timeline dialog
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Action dialogs
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, assignment: null });
  const [actionNotes, setActionNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [liveVerify, setLiveVerify] = useState({ loading: false, data: null, error: null });
  const [bulkApproving, setBulkApproving] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isTeamLeader = user?.role === 'team_leader';
  const isOpsTeam = user?.role === 'ops_team';
  const canCreate = isAdmin || isManager || isTeamLeader || isOpsTeam;

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.gym_id) params.gym_id = filters.gym_id;
      const res = await axiosInstance.get('/supplements/assignments', { params });
      setAssignments(res.data?.assignments || []);
    } catch (error) {
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStacks = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/supplements/stacks');
      setStacks((res.data?.stacks || []).filter(s => s.active !== false));
    } catch {
      // Stacks fetch is non-critical
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  useEffect(() => {
    fetchStacks();
  }, [fetchStacks]);

  // --- Create Assignment ---
  const handleMemberSelected = (memberData) => {
    setCreateForm(prev => ({
      ...prev,
      customer_name: memberData.customer_name || '',
      customer_phone: memberData.customer_phone || '',
      customer_email: memberData.customer_email || '',
      gym_name: memberData.gym_name || '',
      gym_id: memberData.gym_id || '',
      membership_plan: memberData.membership_plan || '',
      membership_start: memberData.membership_start || '',
      membership_expiry: memberData.membership_expiry || '',
      membership_price: memberData.membership_price || 0,
      membership_sold_by: memberData.membership_sold_by || '',
      is_annual: memberData.is_annual || false,
      plan_tier: memberData.plan_tier || (memberData.is_annual ? 'annual' : 'other'),
      manual_entry: memberData.manual_entry || false,
    }));
  };

  const openCreateDialog = () => {
    setCreateForm({
      customer_name: '', customer_phone: '', customer_email: '',
      gym_name: '', gym_id: '', stack_id: '', notes: '',
      membership_plan: '', membership_start: '', membership_expiry: '', membership_price: 0,
      membership_sold_by: '',
      is_annual: false, plan_tier: 'other', manual_entry: false,
    });
    setShowCreateDialog(true);
  };

  const handleCreateAssignment = async () => {
    if (!createForm.customer_name.trim()) { toast.error('Customer name is required'); return; }
    if (!createForm.customer_phone.trim()) { toast.error('Customer phone is required'); return; }
    if (!createForm.gym_name.trim()) { toast.error('Gym name is required — use Member Lookup or enter manually'); return; }
    if (!createForm.stack_id) { toast.error('Please select a stack'); return; }

    setSubmitting(true);
    try {
      await axiosInstance.post('/supplements/assignments', {
        customer_name: createForm.customer_name.trim(),
        customer_phone: createForm.customer_phone.trim(),
        customer_email: createForm.customer_email.trim() || null,
        gym_name: createForm.gym_name.trim(),
        gym_id: createForm.gym_id || createForm.gym_name.trim(),
        stack_id: createForm.stack_id,
        notes: createForm.notes.trim() || null,
        membership_plan: createForm.membership_plan || null,
        membership_start: createForm.membership_start || null,
        membership_expiry: createForm.membership_expiry || null,
        membership_price: createForm.membership_price || null,
        membership_sold_by: createForm.membership_sold_by || null,
        is_annual: createForm.is_annual || false,
        plan_tier: createForm.plan_tier || 'other',
        manual_entry: createForm.manual_entry || false,
      });
      toast.success('Stack assigned successfully');
      setShowCreateDialog(false);
      fetchAssignments();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error?.includes('Insufficient stock')) {
        const shortNames = (detail.short || []).map(i => i.product_name || i.product_id).join(', ');
        toast.error(`Out of stock: ${shortNames || 'some items'}. Please add inventory first.`, { duration: 8000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create assignment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // --- Actions ---
  const openAction = (type, assignment) => {
    setActionDialog({ open: true, type, assignment });
    setActionNotes('');
    setCancelReason('');
    setLiveVerify({ loading: false, data: null, error: null });
    // Auto-fetch live WTF verification for admin approve/reject and shreya verify/reject
    if (['admin_approve', 'admin_reject', 'shreya_verify', 'shreya_reject'].includes(type)) {
      setLiveVerify({ loading: true, data: null, error: null });
      axiosInstance.get(`/supplements/assignments/live-verify/${assignment.id}`)
        .then(res => setLiveVerify({ loading: false, data: res.data, error: null }))
        .catch(err => setLiveVerify({ loading: false, data: null, error: err.response?.data?.detail || 'Verification failed' }));
    }
  };

  const handleAction = async () => {
    const { type, assignment } = actionDialog;
    if (!assignment) return;

    if (type === 'cancel' && !cancelReason.trim()) {
      toast.error('Cancellation reason is required');
      return;
    }
    if (type === 'admin_reject' && !cancelReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    if (type === 'shreya_reject' && !cancelReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    setActionSubmitting(true);
    try {
      let endpoint = '';
      let body = {};
      if (type === 'verify') {
        endpoint = `/supplements/assignments/${assignment.id}/tl-verify`;
        body = { notes: actionNotes.trim() || null };
      } else if (type === 'approve') {
        endpoint = `/supplements/assignments/${assignment.id}/manager-verify`;
        body = { notes: actionNotes.trim() || null };
      } else if (type === 'admin_approve') {
        endpoint = `/supplements/assignments/${assignment.id}/admin-approve`;
        body = { notes: actionNotes.trim() || null };
      } else if (type === 'admin_reject') {
        endpoint = `/supplements/assignments/${assignment.id}/admin-reject`;
        body = { reason: cancelReason.trim() };
      } else if (type === 'shreya_verify') {
        endpoint = `/supplements/assignments/${assignment.id}/shreya-verify`;
        body = { notes: actionNotes.trim() || null };
      } else if (type === 'shreya_reject') {
        endpoint = `/supplements/assignments/${assignment.id}/shreya-reject`;
        body = { reason: cancelReason.trim() };
      } else if (type === 'cancel') {
        endpoint = `/supplements/assignments/${assignment.id}/cancel`;
        body = { reason: cancelReason.trim() };
      }
      await axiosInstance.patch(endpoint, body);
      const messages = {
        verify: 'Assignment TL-verified',
        approve: 'Assignment manager-approved',
        shreya_verify: '✓ Shreya QA verified',
        shreya_reject: 'Sent back for re-approval (Shreya rejected)',
        admin_approve: '✓ Delivery approved by Vishal',
        admin_reject: 'Assignment sent back for re-approval',
        cancel: 'Assignment cancelled',
      };
      toast.success(messages[type]);
      setActionDialog({ open: false, type: null, assignment: null });
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${type} assignment`);
    } finally {
      setActionSubmitting(false);
    }
  };

  // --- Timeline ---
  const openTimeline = (assignment) => {
    setSelectedAssignment(assignment);
    setShowTimeline(true);
  };

  // --- Bulk Approve ---
  const handleBulkApprove = async () => {
    if (!window.confirm(`Bulk approve all ${shreyaVerifiedCount} Shreya-verified assignments?`)) return;
    setBulkApproving(true);
    try {
      const res = await axiosInstance.post('/supplements/assignments/bulk-admin-approve');
      toast.success(res.data.message || 'Bulk approved');
      fetchAssignments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk approve failed');
    } finally {
      setBulkApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  const selectedStack = stacks.find(s => s.id === createForm.stack_id);
  const shreyaVerifiedCount = assignments.filter(a => a.status === 'shreya_verified').length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Stack Assignments</h2>
          <p className="text-sm text-muted-foreground">{assignments.length} assignment{assignments.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && shreyaVerifiedCount > 0 && (
            <Button
              variant="outline"
              className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
              onClick={handleBulkApprove}
              disabled={bulkApproving}
            >
              {bulkApproving
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <ShieldCheck className="w-4 h-4 mr-2" />
              }
              Bulk Approve All ({shreyaVerifiedCount})
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreateDialog} data-testid="create-assignment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Assign Stack
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={filters.status} onValueChange={(val) => setFilters(prev => ({ ...prev, status: val }))}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          placeholder="Filter by gym..."
          value={filters.gym_id}
          onChange={(e) => setFilters(prev => ({ ...prev, gym_id: e.target.value }))}
          className="w-48"
          data-testid="gym-filter"
        />
      </div>

      {/* Assignment Table */}
      {assignments.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ClipboardList className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Assignments</h3>
            <p className="text-muted-foreground">
              {canCreate ? 'Click "Assign Stack" to create the first assignment.' : 'No assignments found.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="text-left px-4 py-3 text-sm font-medium">Customer</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Phone</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Gym</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Stack</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Membership</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Sold By</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium min-w-[220px]">Verification Trail</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">Assigned</th>
                    <th className="text-left px-4 py-3 text-sm font-medium">SLA</th>
                    <th className="text-center px-4 py-3 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {assignments.map((a) => {
                    const sla = getSlaStatus(a);
                    const statusBadge = STATUS_BADGES[a.status] || STATUS_BADGES.assigned;
                    const canVerify = a.status === 'assigned' && (isAdmin || isTeamLeader || isOpsTeam);
                    const canApprove = a.status === 'tl_verified' && (isAdmin || isManager || isOpsTeam);
                    const canShreyaVerify = a.status === 'manager_verified' && (isAdmin || isOpsTeam);
                    const canShreyaReject = a.status === 'manager_verified' && (isAdmin || isOpsTeam);
                    const canAdminApprove = a.status === 'shreya_verified' && isAdmin;
                    const canAdminReject = a.status === 'shreya_verified' && isAdmin;
                    const canCancel = ['assigned', 'tl_verified', 'manager_verified', 'shreya_verified'].includes(a.status) && (isAdmin || isManager || isOpsTeam);
                    const isCancelled = a.status === 'cancelled';

                    return (
                      <tr
                        key={a.id}
                        className={`hover:bg-slate-50 cursor-pointer ${isCancelled ? 'opacity-60' : ''}`}
                        onClick={() => openTimeline(a)}
                        data-testid={`assignment-row-${a.id}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-800">{a.customer_name}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{a.customer_phone}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{a.gym_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{a.stack_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {a.membership_plan ? (
                            <div>
                              <div className="font-medium text-slate-700">{a.membership_plan}</div>
                              {a.membership_start && (
                                <div className="text-xs text-slate-400">
                                  {formatDate(a.membership_start)} → {formatDate(a.membership_expiry)}
                                </div>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {a.membership_sold_by || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className={statusBadge.className}>
                              {statusBadge.label}
                            </Badge>
                            {a.admin_exception && (
                              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 text-[10px]">
                                Admin Exception
                              </Badge>
                            )}
                            {!(a.plan_tier === 'annual' || a.plan_tier === 'half_yearly' || a.is_annual) && !a.admin_exception && a.status !== 'cancelled' && (
                              <Badge variant="outline" className="border-red-300 bg-red-50 text-red-600 text-[10px]">
                                Non-Eligible
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {(() => {
                            const trail = getVerificationTrail(a);
                            if (trail.length === 0) return <span className="text-slate-400">—</span>;
                            return (
                              <div className="space-y-1 max-w-[260px]">
                                {trail.map((s, i) => (
                                  <div key={i} className={`${s.isRejection ? 'bg-red-50 border-l-2 border-red-300' : 'border-l-2 border-slate-200'} pl-2 py-0.5`}>
                                    <div className="flex items-center gap-1">
                                      <span className={`font-semibold ${s.color}`}>{s.label}</span>
                                      <span className="text-slate-500">· {s.name}</span>
                                    </div>
                                    {s.notes && (
                                      <div className="text-slate-600 truncate max-w-[240px]" title={s.notes}>
                                        "{s.notes}"
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          <div>{a.assigned_by_name || '—'}</div>
                          <div className="text-xs text-slate-400">{formatDate(a.created_at)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {sla ? <span className={sla.className}>{sla.text}</span> : '—'}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openTimeline(a)}
                              title="View Timeline"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {canVerify && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={() => openAction('verify', a)}
                                title="TL Verify"
                                data-testid={`verify-btn-${a.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {canApprove && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                                onClick={() => openAction('approve', a)}
                                title="Manager Approve"
                                data-testid={`approve-btn-${a.id}`}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </Button>
                            )}
                            {canShreyaVerify && (
                              <Button
                                variant="ghost" size="sm"
                                className="text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                                onClick={() => openAction('shreya_verify', a)}
                                title="Shreya QA Verify"
                                data-testid={`shreya-verify-btn-${a.id}`}
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </Button>
                            )}
                            {canShreyaReject && (
                              <Button
                                variant="ghost" size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openAction('shreya_reject', a)}
                                title="Shreya Reject"
                                data-testid={`shreya-reject-btn-${a.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                            {canAdminApprove && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                                onClick={() => openAction('admin_approve', a)}
                                title="Vishal Final Approval"
                                data-testid={`admin-approve-btn-${a.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {canAdminReject && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                onClick={() => openAction('admin_reject', a)}
                                title="Vishal Reject"
                                data-testid={`admin-reject-btn-${a.id}`}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                            {canCancel && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openAction('cancel', a)}
                                title="Cancel Assignment"
                                data-testid={`cancel-btn-${a.id}`}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Assignment Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Supplement Stack</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Member Lookup */}
            <div>
              <Button
                variant="outline"
                onClick={() => setShowMemberLookup(true)}
                className="w-full justify-start"
                data-testid="open-member-lookup-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                {createForm.customer_name ? `${createForm.customer_name} (${createForm.customer_phone})` : 'Look Up Member'}
              </Button>
              {createForm.membership_plan && (
                <div className="mt-1 text-xs text-slate-500">
                  Plan: {createForm.membership_plan} | Expiry: {formatDate(createForm.membership_expiry)} | {formatINR(createForm.membership_price)}
                </div>
              )}
            </div>

            {/* Manual fields (editable even after lookup) */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Customer Name *</Label>
                <Input
                  value={createForm.customer_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="Full name"
                  data-testid="create-customer-name"
                />
              </div>
              <div className="space-y-1">
                <Label>Phone *</Label>
                <Input
                  value={createForm.customer_phone}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="Phone number"
                  data-testid="create-customer-phone"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  value={createForm.customer_email}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="Email (optional)"
                />
              </div>
              <div className="space-y-1">
                <Label>Gym *</Label>
                <Input
                  value={createForm.gym_name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, gym_name: e.target.value }))}
                  placeholder="Gym name"
                />
              </div>
            </div>

            {/* Stack Selection */}
            <div className="space-y-1">
              <Label>Supplement Stack *</Label>
              <Select
                value={createForm.stack_id}
                onValueChange={(val) => setCreateForm(prev => ({ ...prev, stack_id: val }))}
              >
                <SelectTrigger data-testid="stack-select">
                  <SelectValue placeholder="Select a stack" />
                </SelectTrigger>
                <SelectContent>
                  {stacks.map(stack => (
                    <SelectItem key={stack.id} value={stack.id}>
                      {stack.name} — {formatINR(stack.total_cost)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStack && (
                <p className="text-xs text-slate-500 mt-1">
                  {selectedStack.items?.length || 0} items, total: {formatINR(selectedStack.total_cost)}
                </p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>

          {/* Plan eligibility warning */}
          {createForm.customer_name && !(createForm.plan_tier === 'annual' || createForm.plan_tier === 'half_yearly') && (
            <div className={`border rounded-lg p-3 ${user?.role === 'admin' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className={`w-4 h-4 ${user?.role === 'admin' ? 'text-amber-600' : 'text-red-600'}`} />
                <span className={`text-sm font-medium ${user?.role === 'admin' ? 'text-amber-700' : 'text-red-700'}`}>
                  {createForm.manual_entry ? 'Manual entry — membership not verified' : 'Not an eligible plan member'}
                </span>
              </div>
              <p className={`text-xs ${user?.role === 'admin' ? 'text-amber-600' : 'text-red-600'}`}>
                {user?.role === 'admin'
                  ? 'You can create this as an admin exception. It will be flagged for audit.'
                  : 'Only annual and half-yearly plan members are eligible for supplement stacks. Contact Vishal for exceptions.'}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateAssignment}
              disabled={submitting || (createForm.customer_name && !(createForm.plan_tier === 'annual' || createForm.plan_tier === 'half_yearly') && user?.role !== 'admin')}
              data-testid="submit-assignment-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitting ? 'Assigning...' : (createForm.plan_tier === 'annual' || createForm.plan_tier === 'half_yearly') ? 'Assign Stack' : 'Assign (Admin Exception)'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Lookup Dialog */}
      <MemberLookupDialog
        open={showMemberLookup}
        onClose={() => setShowMemberLookup(false)}
        onSelectMember={handleMemberSelected}
        gymId={createForm.gym_id || undefined}
      />

      {/* Action Confirm Dialog (Verify / Approve / Cancel) */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => {
        if (!open) setActionDialog({ open: false, type: null, assignment: null });
      }}>
        <DialogContent className={['admin_approve', 'admin_reject', 'shreya_verify', 'shreya_reject'].includes(actionDialog.type) ? 'max-w-lg' : 'max-w-sm'}>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'verify' && 'TL Verify Assignment'}
              {actionDialog.type === 'approve' && 'Manager Approve Assignment'}
              {actionDialog.type === 'shreya_verify' && 'Shreya QA Verification'}
              {actionDialog.type === 'shreya_reject' && 'Shreya Reject — Send Back'}
              {actionDialog.type === 'admin_approve' && '✓ Vishal Final Approval'}
              {actionDialog.type === 'admin_reject' && 'Reject & Send Back'}
              {actionDialog.type === 'cancel' && 'Cancel Assignment'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {actionDialog.assignment && (
              <p className="text-sm text-slate-600">
                {actionDialog.assignment.customer_name} — {actionDialog.assignment.stack_name || 'Stack'}
              </p>
            )}

            {/* Live WTF API verification panel (admin approve/reject and shreya verify/reject) */}
            {(['admin_approve', 'admin_reject', 'shreya_verify', 'shreya_reject'].includes(actionDialog.type)) && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-800 text-white px-3 py-2 text-xs font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-3 h-3" />
                  LIVE WTF API VERIFICATION
                  {liveVerify.data && (
                    <span className="ml-auto text-slate-400 font-normal">
                      {new Date(liveVerify.data.fetched_at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </span>
                  )}
                </div>
                <div className="p-3">
                  {liveVerify.loading && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Fetching live data from WTF API...
                    </div>
                  )}
                  {liveVerify.error && (
                    <div className="flex items-center gap-2 text-sm text-red-600">
                      <AlertTriangle className="w-4 h-4" />
                      {liveVerify.error}
                    </div>
                  )}
                  {liveVerify.data && !liveVerify.loading && (
                    <div className="space-y-2">
                      {!liveVerify.data.verified ? (
                        <div className="text-sm text-red-600 font-medium flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {liveVerify.data.reason || 'No subscription found'}
                        </div>
                      ) : (
                        <>
                          <div className={`text-xs font-bold px-2 py-1 rounded inline-flex items-center gap-1 ${liveVerify.data.has_annual ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            <ShieldCheck className="w-3 h-3" />
                            {liveVerify.data.has_annual ? 'ANNUAL MEMBER CONFIRMED' : 'NO ANNUAL SUBSCRIPTION'}
                          </div>
                          {liveVerify.data.subscriptions?.map((sub, i) => (
                            <div key={i} className={`text-xs rounded p-2 space-y-0.5 ${sub.is_annual ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                              <div className="font-medium text-slate-800">{sub.plan_name}</div>
                              <div className="text-slate-600">{sub.gym_name}</div>
                              <div className="text-slate-500">{formatDate(sub.start_date)} → {formatDate(sub.expire_date)} · ₹{sub.price?.toLocaleString('en-IN')}</div>
                              {sub.sold_by && <div className="text-slate-400">Sold by: {sub.sold_by}</div>}
                              <div className={`font-semibold ${sub.is_annual ? 'text-green-700' : 'text-slate-500'}`}>
                                {sub.is_annual ? '✓ Annual' : 'Non-Annual'} · Status: {sub.status}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Non-eligible plan gate warning on verify/approve (annual + half_yearly both eligible) */}
            {actionDialog.assignment && !['annual', 'half_yearly'].includes(actionDialog.assignment.plan_tier) && !['cancel', 'admin_reject', 'shreya_reject'].includes(actionDialog.type) && (
              <div className={`border rounded-lg p-3 ${user?.role === 'admin' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 ${user?.role === 'admin' ? 'text-amber-600' : 'text-red-600'}`} />
                  <span className={`text-sm font-medium ${user?.role === 'admin' ? 'text-amber-700' : 'text-red-700'}`}>
                    Non-eligible plan member
                  </span>
                </div>
                <p className={`text-xs mt-1 ${user?.role === 'admin' ? 'text-amber-600' : 'text-red-600'}`}>
                  {user?.role === 'admin'
                    ? 'Admin exception — this will be flagged for audit.'
                    : 'Only admin (Vishal) can verify/approve non-eligible plan assignments.'}
                </p>
              </div>
            )}
            {(['cancel', 'admin_reject', 'shreya_reject'].includes(actionDialog.type)) ? (
              <div className="space-y-1">
                <Label>
                  {actionDialog.type === 'admin_reject' ? 'Rejection reason *'
                    : actionDialog.type === 'shreya_reject' ? 'Rejection reason *'
                    : 'Reason for cancellation *'}
                </Label>
                <Textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={
                    actionDialog.type === 'admin_reject'
                      ? 'Why is this delivery being rejected? Manager must re-verify before resubmitting.'
                      : actionDialog.type === 'shreya_reject'
                      ? 'Why is this assignment being rejected? Manager must re-verify before resubmitting.'
                      : 'Why is this assignment being cancelled?'}
                  rows={3}
                  data-testid="cancel-reason-input"
                />
              </div>
            ) : (
              <div className="space-y-1">
                <Label>{actionDialog.type === 'admin_approve' ? 'Approval notes (optional)' : 'Notes (optional)'}</Label>
                <Textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder={actionDialog.type === 'admin_approve'
                    ? 'Any notes for the ops team about this delivery...'
                    : 'Optional notes...'}
                  rows={2}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, type: null, assignment: null })}>
              Close
            </Button>
            <Button
              variant={['cancel', 'admin_reject', 'shreya_reject'].includes(actionDialog.type) ? 'destructive' : 'default'}
              onClick={handleAction}
              disabled={actionSubmitting || (!['cancel', 'admin_approve', 'admin_reject', 'shreya_verify', 'shreya_reject'].includes(actionDialog.type) && actionDialog.assignment && !['annual', 'half_yearly'].includes(actionDialog.assignment.plan_tier) && !actionDialog.assignment.admin_exception && user?.role !== 'admin')}
              data-testid="confirm-action-btn"
            >
              {actionSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {actionSubmitting ? 'Processing...'
                : actionDialog.type === 'verify' ? 'TL Verify'
                : actionDialog.type === 'approve' ? 'Manager Approve'
                : actionDialog.type === 'shreya_verify' ? '✓ Shreya QA Verify'
                : actionDialog.type === 'shreya_reject' ? 'Reject & Send Back'
                : actionDialog.type === 'admin_approve' ? '✓ Approve Delivery'
                : actionDialog.type === 'admin_reject' ? 'Reject & Send Back'
                : 'Cancel Assignment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assignment Timeline</DialogTitle>
          </DialogHeader>
          {selectedAssignment && (
            <div>
              <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm">
                <div className="font-medium">{selectedAssignment.customer_name}</div>
                <div className="text-slate-500">
                  {selectedAssignment.customer_phone} | {selectedAssignment.gym_name || '—'} | {selectedAssignment.stack_name || '—'}
                </div>
              </div>
              <VisualTimeline assignmentId={selectedAssignment.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
